/**
 * Each ability record's cooldown as the server's last `used` for it said (P2.8-T12, ADR-091 decision 14), kept by the
 * record's number in typed arrays allocated once. The bar's item slot takes its record's cooldown from here when it
 * fills, so a slot that empties and fills again inside a cooldown, or a `used` that arrives while the slot is empty,
 * shows what is left. Times are whole ms from the bar's origin, as the bar's own are.
 */
export class RecordCooldowns {
  private readonly ends: Int32Array;
  private readonly lengths: Int32Array;

  /**
   * @param records How many ability records the rules hold; numbers run from 1 to this.
   */
  public constructor(records: number) {
    this.ends = new Int32Array(records + 1);
    this.lengths = new Int32Array(records + 1);
  }

  /**
   * Notes a record's cooldown. A number the rules do not hold is passed over.
   *
   * @param record The record's number.
   * @param end When the cooldown ends, in ms from the origin; 0 for none.
   * @param length The cooldown's length in ms.
   */
  public note(record: number, end: number, length: number): void {
    if (record > 0 && record < this.ends.length) {
      this.ends[record] = end;
      this.lengths[record] = length;
    }
  }

  /**
   * When a record's cooldown ends.
   *
   * @param record The record's number.
   * @returns The end in ms from the origin; 0 for none and for a number the rules do not hold.
   */
  public endOf(record: number): number {
    return this.ends[record] ?? 0;
  }

  /**
   * How long a record's cooldown is.
   *
   * @param record The record's number.
   * @returns The length in ms; 0 for none and for a number the rules do not hold.
   */
  public lengthOf(record: number): number {
    return this.lengths[record] ?? 0;
  }

  /**
   * Forgets every cooldown, as a session ends.
   */
  public reset(): void {
    this.ends.fill(0);
    this.lengths.fill(0);
  }
}
